/* Search iTunes podcast catalog, pick a podcast, and subscribe via sources.create. */

import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Podcast, Star, Mic, Globe, Calendar, Shield, ExternalLink, Clock, Music, FileText, Link, TrendingUp } from "lucide-react";
import { usePodcastSearch } from "@/hooks/usePodcastSearch";
import { SourceTagSelect } from "@/components/sources/SourceTagSelect";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import type { PodcastSearchResult, PodcastEpisodePreview, TopChartPodcast } from "@/types/podcastSearch";

const GENRE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "technology", label: "Technology" },
  { value: "business", label: "Business" },
  { value: "comedy", label: "Comedy" },
  { value: "news", label: "News" },
  { value: "society", label: "Society" },
  { value: "education", label: "Education" },
  { value: "health", label: "Health" },
  { value: "science", label: "Science" },
  { value: "sports", label: "Sports" },
  { value: "trueCrime", label: "True Crime" },
  { value: "history", label: "History" },
  { value: "arts", label: "Arts" },
  { value: "fiction", label: "Fiction" },
] as const;

interface PodcastSearchFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

export function PodcastSearchForm({ onSuccess, footerContainer }: PodcastSearchFormProps) {
  const { results, isSearching, error: searchError, search } = usePodcastSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<PodcastSearchResult | null>(null);
  const [episodes, setEpisodes] = useState<PodcastEpisodePreview[]>([]);
  const [isFetchingEpisodes, setIsFetchingEpisodes] = useState(false);
  const [episodesError, setEpisodesError] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [starred, setStarred] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [chartPodcasts, setChartPodcasts] = useState<TopChartPodcast[]>([]);
  const [isFetchingCharts, setIsFetchingCharts] = useState(false);
  const [chartsGenre, setChartsGenre] = useState("all");
  const [showCharts, setShowCharts] = useState(false);

  const createSource = useMutation(api.sources.create);
  const fetchEpisodesAction = useAction(api.podcastSearch.fetchEpisodes);
  const fetchTopChartsAction = useAction(api.podcastSearch.fetchTopCharts);
  const availableTags = useQuery(api.tags.getAvailableTags) ?? [];
  const addTag = useMutation(api.tags.addTag);
  const portalTargetRef = useRef<HTMLDivElement>(null);

  const loadEpisodes = useCallback(async (feedUrl: string) => {
    setIsFetchingEpisodes(true);
    setEpisodesError(null);
    setEpisodes([]);
    try {
      const data = await fetchEpisodesAction({ feedUrl });
      setEpisodes(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load episodes";
      setEpisodesError(message);
    } finally {
      setIsFetchingEpisodes(false);
    }
  }, [fetchEpisodesAction]);

  const loadCharts = useCallback(async (genre: string) => {
    setIsFetchingCharts(true);
    try {
      const data = await fetchTopChartsAction({ genre, limit: 25 });
      setChartPodcasts(data);
    } catch {
      setChartPodcasts([]);
    } finally {
      setIsFetchingCharts(false);
    }
  }, [fetchTopChartsAction]);

  function handleToggleCharts() {
    if (!showCharts) {
      setShowCharts(true);
      setSelected(null);
      setEpisodes([]);
      loadCharts(chartsGenre);
    } else {
      setShowCharts(false);
    }
  }

  function handleGenreChange(genre: string) {
    setChartsGenre(genre);
    loadCharts(genre);
  }

  function handleSelectChartPodcast(podcast: TopChartPodcast) {
    // Search by name to get the full PodcastSearchResult with feedUrl
    setShowCharts(false);
    setSearchQuery(podcast.name);
    search(podcast.name);
    setSelected(null);
    setEpisodes([]);
  }

  function handleSearch() {
    search(searchQuery);
    setSelected(null);
    setEpisodes([]);
    setEpisodesError(null);
    setShowCharts(false);
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  }

  function handleSelectPodcast(podcast: PodcastSearchResult) {
    setSelected(podcast);
    setSubmitError(null);
    setEpisodes([]);
    setEpisodesError(null);
    if (podcast.feedUrl) {
      loadEpisodes(podcast.feedUrl);
    }
  }

  async function handleCreateTag(tag: string) {
    await addTag({ tag });
  }

  const hasFeedUrl = selected?.feedUrl != null;
  const canSubmit = selected !== null && hasFeedUrl && !isSubmitting;

  async function handleSubmit() {
    if (!selected || !selected.feedUrl) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createSource({
        name: selected.name,
        type: "podcast",
        url: selected.feedUrl,
        tags: tags.length > 0 ? tags : undefined,
        starred: starred || undefined,
        thumbnail: selected.artworkUrl600 || undefined,
        description: selected.author || undefined,
        episodeCount: selected.episodeCount || undefined,
      });
      posthog.capture(Events.SOURCE_ADDED, { sourceType: "podcast", starred, podcastName: selected.name });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to subscribe";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      <div ref={portalTargetRef} className="absolute" />

      {/* Search section */}
      <div className="space-y-2">
        <Label>Search Podcasts</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Search by podcast name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          <Button
            variant="outline"
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
          >
            {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>
        {searchError && <p className="text-destructive text-sm">{searchError}</p>}

      </div>

      {/* Top Charts toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleToggleCharts}
        className="w-full"
      >
        <TrendingUp className="size-4" />
        {showCharts ? "Hide Top Charts" : "Browse Top Charts"}
      </Button>

      {/* Top Charts section */}
      {showCharts && (
        <div className="space-y-3">
          {/* Genre filter pills */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
            {GENRE_OPTIONS.map((genre) => (
              <button
                key={genre.value}
                type="button"
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs transition-colors ${
                  chartsGenre === genre.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                onClick={() => handleGenreChange(genre.value)}
              >
                {genre.label}
              </button>
            ))}
          </div>

          {/* Chart loading */}
          {isFetchingCharts && (
            <div className="flex items-center gap-2 py-4 justify-center">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Loading charts...</span>
            </div>
          )}

          {/* Chart results grid */}
          {!isFetchingCharts && chartPodcasts.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
              {chartPodcasts.map((podcast, i) => (
                <button
                  key={podcast.itunesId}
                  type="button"
                  className="flex flex-col items-center gap-1 p-2 rounded-md border border-border cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleSelectChartPodcast(podcast)}
                >
                  <div className="relative">
                    {podcast.artwork ? (
                      <img
                        src={podcast.artwork}
                        alt={podcast.name}
                        className="size-14 rounded-md object-cover bg-muted"
                      />
                    ) : (
                      <div className="size-14 rounded-md bg-muted flex items-center justify-center">
                        <Podcast className="size-5 text-muted-foreground/50" />
                      </div>
                    )}
                    <span className="absolute -top-1 -left-1 size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                    {podcast.name}
                  </span>
                  <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                    {podcast.author}
                  </span>
                </button>
              ))}
            </div>
          )}

          {!isFetchingCharts && chartPodcasts.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-3">No chart results found.</p>
          )}
        </div>
      )}

      {/* Search results — horizontal scroll of artwork cards */}
      {results.length > 0 && !showCharts && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {results.map((podcast) => (
            <button
              key={podcast.itunesId}
              type="button"
              className={`flex flex-col items-center gap-1 p-2 rounded-md border cursor-pointer hover:bg-muted/50 shrink-0 w-[90px] ${selected?.itunesId === podcast.itunesId ? "border-primary bg-muted/50" : "border-border"}`}
              onClick={() => handleSelectPodcast(podcast)}
            >
              {podcast.artworkUrl100 ? (
                <img
                  src={podcast.artworkUrl100}
                  alt={podcast.name}
                  className="size-16 rounded-md object-cover bg-muted"
                />
              ) : (
                <div className="size-16 rounded-md bg-muted flex items-center justify-center">
                  <Podcast className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {podcast.name}
              </span>
              <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                {podcast.author}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Selected podcast detail card — shows all available iTunes fields */}
      {selected && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          {/* Header: artwork + basic info */}
          <div className="flex gap-3">
            {selected.artworkUrl600 ? (
              <img
                src={selected.artworkUrl600}
                alt={selected.name}
                className="size-20 rounded-md object-cover bg-muted shrink-0"
              />
            ) : (
              <div className="size-20 rounded-md bg-muted flex items-center justify-center shrink-0">
                <Podcast className="size-8 text-muted-foreground/50" />
              </div>
            )}
            <div className="flex flex-col gap-1.5 min-w-0">
              <p className="text-sm font-semibold leading-tight">{selected.name}</p>
              <p className="text-xs text-muted-foreground">{selected.author}</p>
              <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                {selected.episodeCount > 0 && (
                  <span className="inline-flex items-center gap-0.5">
                    <Mic className="size-3" />
                    {selected.episodeCount} episodes
                  </span>
                )}
                {selected.country && (
                  <span className="inline-flex items-center gap-0.5">
                    <Globe className="size-3" />
                    {selected.country}
                  </span>
                )}
                {selected.releaseDate && (
                  <span className="inline-flex items-center gap-0.5">
                    <Calendar className="size-3" />
                    {new Date(selected.releaseDate).toLocaleDateString()}
                  </span>
                )}
                {selected.contentAdvisoryRating && (
                  <span className="inline-flex items-center gap-0.5">
                    <Shield className="size-3" />
                    {selected.contentAdvisoryRating}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Genres */}
          {selected.genres.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selected.genres.map((genre) => (
                <Badge key={genre} variant="secondary" className="text-[10px] px-1.5 py-0">
                  {genre}
                </Badge>
              ))}
            </div>
          )}

          {/* All fields detail table */}
          <div className="text-xs space-y-1 border-t border-border pt-2">
            <DetailRow label="iTunes ID" value={String(selected.itunesId)} />
            {selected.collectionId && <DetailRow label="Collection ID" value={String(selected.collectionId)} />}
            {selected.collectionName && <DetailRow label="Collection Name" value={selected.collectionName} />}
            <DetailRow label="Primary Genre" value={selected.primaryGenreName} />
            {selected.genreIds.length > 0 && <DetailRow label="Genre IDs" value={selected.genreIds.join(", ")} />}
            <DetailRow label="Feed URL" value={selected.feedUrl ?? "N/A"} />
            <DetailRow label="Apple Podcasts URL" value={selected.applePodcastsUrl} isUrl />
            {selected.trackViewUrl && <DetailRow label="Track View URL" value={selected.trackViewUrl} isUrl />}
            <DetailRow label="Artwork 30px" value={selected.artworkUrl30} isUrl />
            <DetailRow label="Artwork 60px" value={selected.artworkUrl60} isUrl />
            <DetailRow label="Artwork 100px" value={selected.artworkUrl100} isUrl />
            <DetailRow label="Artwork 600px" value={selected.artworkUrl600} isUrl />
            {selected.collectionExplicitness && <DetailRow label="Collection Explicitness" value={selected.collectionExplicitness} />}
            {selected.trackExplicitness && <DetailRow label="Track Explicitness" value={selected.trackExplicitness} />}
          </div>

          {/* Episode preview */}
          {hasFeedUrl && (
            <div className="space-y-2 border-t border-border pt-2">
              <p className="text-xs font-medium text-muted-foreground">
                Latest Episodes {episodes.length > 0 && `(${episodes.length})`}
              </p>
              {isFetchingEpisodes && (
                <div className="flex items-center gap-2 py-3 justify-center">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Loading episodes...</span>
                </div>
              )}
              {episodesError && (
                <p className="text-destructive text-xs">{episodesError}</p>
              )}
              {episodes.length > 0 && (
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {episodes.map((ep, i) => (
                    <EpisodeRow key={`${ep.link}-${i}`} episode={ep} />
                  ))}
                </div>
              )}
            </div>
          )}

          {!hasFeedUrl && (
            <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1.5">
              This podcast is exclusive to Apple Podcasts and doesn't provide an RSS feed URL. It can't be subscribed to via FeedKiwi.
            </p>
          )}

          {/* Tags & Star */}
          {hasFeedUrl && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStarred(!starred)}
                  className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                >
                  <Star className={`size-5 ${starred ? "fill-current text-foreground" : ""}`} />
                </button>
                <span className="text-xs text-muted-foreground">
                  {starred ? "Starred — new episodes go to queue" : "Star to prioritize"}
                </span>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <SourceTagSelect
                  selectedTags={tags}
                  onTagsChange={setTags}
                  availableTags={availableTags}
                  onCreateTag={handleCreateTag}
                  portalContainer={portalTargetRef}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {submitError && <p className="text-destructive text-sm">{submitError}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          {selected && !hasFeedUrl ? "No RSS Feed Available" : "Subscribe"}
        </Button>,
        footerContainer,
      ) : (
        <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          {selected && !hasFeedUrl ? "No RSS Feed Available" : "Subscribe"}
        </Button>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${(bytes / 1_000).toFixed(0)} KB`;
}

function EpisodeRow({ episode }: { episode: PodcastEpisodePreview }) {
  return (
    <div className="flex gap-2 p-2 rounded-md border border-border">
      {episode.thumbnail ? (
        <img
          src={episode.thumbnail}
          alt={episode.title}
          className="size-10 rounded-sm object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="size-10 rounded-sm bg-muted flex items-center justify-center shrink-0">
          <Music className="size-4 text-muted-foreground/50" />
        </div>
      )}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <p className="text-xs font-medium leading-tight line-clamp-1">{episode.title}</p>
        <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
          {episode.publishedAt > 0 && (
            <span>{new Date(episode.publishedAt).toLocaleDateString()}</span>
          )}
          {episode.duration != null && episode.duration > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <Clock className="size-2.5" />
              {formatDuration(episode.duration)}
            </span>
          )}
          {episode.audioSize != null && episode.audioSize > 0 && (
            <span>{formatFileSize(episode.audioSize)}</span>
          )}
          {episode.audioType && (
            <span>{episode.audioType}</span>
          )}
          {episode.audioUrl && (
            <a href={episode.audioUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">
              <Music className="size-2.5" />
              audio
            </a>
          )}
          {episode.link && (
            <a href={episode.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">
              <Link className="size-2.5" />
              link
            </a>
          )}
          {episode.transcriptUrl && (
            <a href={episode.transcriptUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">
              <FileText className="size-2.5" />
              transcript
            </a>
          )}
          {episode.contentEncoded && (
            <span className="inline-flex items-center gap-0.5">
              <FileText className="size-2.5" />
              show notes
            </span>
          )}
        </div>
        {episode.description && (
          <p className="text-[10px] text-muted-foreground line-clamp-2 leading-tight">
            {episode.description.replace(/<[^>]*>/g, "")}
          </p>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, isUrl }: { label: string; value: string; isUrl?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0 w-36">{label}</span>
      {isUrl && value !== "N/A" ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary truncate inline-flex items-center gap-0.5 hover:underline"
        >
          <ExternalLink className="size-3 shrink-0" />
          <span className="truncate">{value}</span>
        </a>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </div>
  );
}
